import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SelectOption } from '@app/app/shared/styleguide/components/selects/sg-select/sg-select.component';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { TranslocoService } from '@ngneat/transloco';
import * as moment from 'moment';
import { LocalTimeHelper } from '../../../../helpers/local-time-helper';
import { ActivityRequest, BooleanFilter, BooleanQueryRequest, BooleanQueryType } from '../../../../models/admin/boolean-filters.model';

type ActivityFilterType = 'SINCE' | 'FOR_THE_LAST' | 'IN_RANGE';
type ActivityPeriodType = 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';

@Component({
  selector: 'ptl-v3-activity-date-filter',
  templateUrl: './v3-activity-date-filter.component.html',
  styleUrls: ['./v3-activity-date-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V3ActivityDateFilterComponent implements OnInit {
  @Input() selectedFilters: BooleanFilter[];
  @Output() filterDeleted = new EventEmitter<void>();
  @Output() filterSaved = new EventEmitter<BooleanFilter>();

  activityIs = true;
  filterType: ActivityFilterType = 'SINCE';

  date: Date;
  periodType: ActivityPeriodType = 'DAY';
  quantity = 10;

  startDate: Date;
  endDate: Date;

  activityIsOptions: SelectOption<boolean>[] = [];
  filterTypeOptions: SelectOption<ActivityFilterType>[] = [];
  periodTypeOptions: SelectOption<ActivityPeriodType>[] = [];

  constructor(private translocoService: TranslocoService) {}

  ngOnInit() {
    this.date = new Date(new Date().setUTCHours(0, 0, 0, 0));
    this.activityIsOptions = [
      { value: true, label: this.translocoService.translate(marker('translations.filter.has')) },
      { value: false, label: this.translocoService.translate(marker('translations.filter.hasNot')) },
    ];
    this.filterTypeOptions = [
      { value: 'SINCE', label: this.translocoService.translate(marker('translations.filter.activity.since')) },
      { value: 'FOR_THE_LAST', label: this.translocoService.translate(marker('translations.filter.activity.forTheLast')) },
      { value: 'IN_RANGE', label: this.translocoService.translate(marker('translations.global.ariaLabels.inRange')) },
    ];
    this.periodTypeOptions = [
      { value: 'DAY', label: this.translocoService.translate(marker('translations.filter.activity.days')) },
      { value: 'WEEK', label: this.translocoService.translate(marker('translations.filter.activity.weeks')) },
      { value: 'MONTH', label: this.translocoService.translate(marker('translations.filter.activity.months')) },
      { value: 'YEAR', label: this.translocoService.translate(marker('translations.filter.activity.years')) },
    ];
    this.getSettingsFromFilters();
  }

  get sinceDateValue(): string {
    return this.date ? moment(this.date).format('YYYY-MM-DD') : '';
  }

  get startDateValue(): string {
    return this.startDate ? moment(this.startDate).format('YYYY-MM-DD') : '';
  }

  get endDateValue(): string {
    return this.endDate ? moment(this.endDate).format('YYYY-MM-DD') : '';
  }

  dateChanged(date: Date) {
    this.date = date;
  }

  onSinceDateChange(value: string) {
    const date = this.parseDateInputValue(value);
    if (date) {
      this.dateChanged(date);
    }
  }

  onStartDateChange(value: string) {
    const date = this.parseDateInputValue(value);
    if (date) {
      this.startDate = date;
      this.applyRangeIfComplete();
    }
  }

  onEndDateChange(value: string) {
    const date = this.parseDateInputValue(value);
    if (date) {
      this.endDate = date;
      this.applyRangeIfComplete();
    }
  }

  deleteFilter() {
    this.filterDeleted.emit();
  }

  applyFilter() {
    if (this.filterType === 'IN_RANGE' && (!this.startDate || !this.endDate)) {
      return;
    }
    const request = this.getRequest();
    const message = this.getMessage();
    this.filterSaved.emit({ request: request, message: message, type: 'ACTIVITY' });
  }

  private applyRangeIfComplete() {
    if (this.filterType === 'IN_RANGE' && this.startDate && this.endDate) {
      this.applyFilter();
    }
  }

  private parseDateInputValue(value: string): Date | null {
    if (!value) {
      return null;
    }
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private getRequest(): BooleanQueryRequest {
    const request: ActivityRequest = {
      type: BooleanQueryType.ACTIVITY,
      operator: this.activityIs ? 'AFTER' : 'BEFORE',
      time: this.getRequestTime().format(),
      startDate: this.filterType === 'IN_RANGE' ? moment(this.startDate).format() : undefined,
      endDate: this.filterType === 'IN_RANGE' ? moment(this.endDate.setHours(23, 59, 59, 59)).format() : undefined,
      frontendType: this.filterType,
      periodType: this.periodType,
      quantity: this.quantity,
    };
    return request as BooleanQueryRequest;
  }

  private getMessage(): string {
    const activityTranslation = this.activityIs ? marker('translations.filter.has') : marker('translations.filter.hasNot');

    const pre = `${this.translocoService.translate(activityTranslation)} ${this.translocoService.translate('translations.filter.activity.beenActive')}`;

    switch (this.filterType) {
      case 'SINCE':
        return `${pre} ${this.translocoService.translate('translations.filter.activity.since')} ${LocalTimeHelper.getLocalDateTime(
          this.date,
        ).format('D MMM YYYY')}`;

      case 'FOR_THE_LAST':
        return `${pre} ${this.translocoService.translate('translations.filter.activity.forTheLast')} ${this.quantity} ${this.getForTheLastMessageEnding()}`;

      case 'IN_RANGE':
        return this.translocoService
          .translate(
            this.activityIs
              ? marker('translations.filter.activity.hasBeenActive')
              : marker('translations.filter.activity.hasNotBeenActive'),
          )
          .replace('{startDate}', this.formatDate(this.startDate))
          .replace('{endDate}', this.formatDate(this.endDate));

      default:
        return '';
    }
  }

  private formatDate(date: Date | null): string {
    if (!date) return '';
    return LocalTimeHelper.getLocalDateTime(date).format('MMM D, YYYY');
  }

  private getForTheLastMessageEnding(): string {
    const singularPluralPostfix = this.quantity === 1 ? '' : 's';
    switch (this.periodType) {
      case 'DAY':
        return this.translocoService.translate(marker('translations.filter.activity.day') + singularPluralPostfix);
      case 'WEEK':
        return this.translocoService.translate(marker('translations.filter.activity.week') + singularPluralPostfix);
      case 'MONTH':
        return this.translocoService.translate(marker('translations.filter.activity.month') + singularPluralPostfix);
      case 'YEAR':
        return this.translocoService.translate(marker('translations.filter.activity.year') + singularPluralPostfix);
    }
  }

  private getRequestTime() {
    switch (this.filterType) {
      case 'SINCE':
        return moment(this.date);
      case 'FOR_THE_LAST':
        return moment().startOf('day').subtract(this.quantity, this.getPeriodDuration());
      case 'IN_RANGE':
        return moment();
    }
  }

  private getPeriodDuration(): 'days' | 'weeks' | 'months' | 'years' {
    return (this.periodType.toLowerCase() + 's') as 'days' | 'weeks' | 'months' | 'years';
  }

  private getSettingsFromFilters() {
    if (this.selectedFilters) {
      const activityFilter = this.selectedFilters.find((f) => f.type === 'ACTIVITY');
      if (activityFilter) {
        const booleanRequest = activityFilter.request as ActivityRequest;
        this.activityIs = booleanRequest.operator === 'AFTER';
        if (booleanRequest.frontendType === 'FOR_THE_LAST') {
          this.filterType = 'FOR_THE_LAST';
          this.periodType = booleanRequest.periodType;
          this.quantity = booleanRequest.quantity;
        } else if (booleanRequest.frontendType === 'SINCE') {
          this.filterType = 'SINCE';
          this.date = new Date(booleanRequest.time);
        } else if (booleanRequest.frontendType === 'IN_RANGE') {
          this.filterType = 'IN_RANGE';
          this.startDate = new Date(booleanRequest.startDate);
          this.endDate = new Date(booleanRequest.endDate);
        }
      }
    }
  }
}
